// Syft ACP - Lib <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import { useCallback } from 'react'
import type { ClipboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { notify } from 'syft-acp-core/actions/notifications'
import type { CountryCode } from 'syft-acp-core/lib/i18n'
import { handleRegionIdPaste } from 'syft-acp-util/regionId'

/**
 * Returns a paste handler that normalises data-warehouse IDs pasted into an
 * input — stripping thousands separators and a US/UK suffix — and raises a
 * toast when the suffix does not match the current ACP region.
 *
 * The handler takes the paste event plus an `applyValue` callback so it can be
 * shared across input types (e.g. a final-form field's `input.onChange`).
 */
export const useRegionIdPaste = () => {
  // Source the region from Redux so every input agrees on the current ACP region.
  const region = useSelector((state: { app: { countryCode: CountryCode } }) => state.app.countryCode)
  const dispatch = useDispatch()

  return useCallback(
    (event: ClipboardEvent<HTMLInputElement>, applyValue: (value: string) => void) =>
      handleRegionIdPaste(event, {
        region,
        applyValue,
        warn: warning => dispatch(notify('warning', { ...warning, autoDismiss: 6 })),
      }),
    [region, dispatch],
  )
}
